/**
 * Sieve — Rapid mode (§10). One number at a time, Prime / Not prime, untimed.
 * Streak continues until the first wrong answer. Best streak stored locally.
 *
 * Rapid uses a seeded stream so a session is reproducible for tests, but each
 * new run seeds from the current best + a rotating salt so players see variety.
 */

import { isPrime, factorString, MIN_NUMBER, MAX_NUMBER } from '../engine/primes';
import { makeRng, type Rng } from '../engine/rng';

export interface RapidState {
  rng: Rng;
  current: number;
  streak: number;
  best: number;
  /** Set when the run ends: the number missed and its factorisation (§10). */
  lastMiss: { number: number; factorisation: string } | null;
  over: boolean;
}

function draw(rng: Rng): number {
  return MIN_NUMBER + rng.int(MAX_NUMBER - MIN_NUMBER + 1);
}

export function startRapid(best: number, seed: number): RapidState {
  const rng = makeRng(seed);
  return { rng, current: draw(rng), streak: 0, best, lastMiss: null, over: false };
}

/**
 * Answers the current number. Returns the updated state. On a correct answer
 * the streak grows and a new number is drawn; on a wrong answer the run ends
 * and the missed number's factorisation is exposed (§10).
 */
export function answer(state: RapidState, saysPrime: boolean): RapidState {
  if (state.over) return state;
  const correct = isPrime(state.current) === saysPrime;
  if (correct) {
    state.streak += 1;
    if (state.streak > state.best) state.best = state.streak;
    state.current = draw(state.rng);
  } else {
    state.over = true;
    state.lastMiss = {
      number: state.current,
      factorisation: isPrime(state.current) ? 'prime' : factorString(state.current),
    };
  }
  return state;
}
